import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Loader2,
  ChevronRight,
  FileText,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, StatusPill, fmtDate, EmptyState } from "./_shared";
import Avatar from "@/components/Avatar";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const LIMIT = 25;
const ROLE_OPTIONS = ["", "admin", "user"];
const PROVIDER_OPTIONS = ["", "password", "google", "both"];

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [authProvider, setAuthProvider] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  // user_id currently being impersonated (button → spinner state)
  const [impersonating, setImpersonating] = useState(null);

  const openAsClient = async (u) => {
    // Confirm because opening in the same browser will swap cookies
    // and log the admin out of admin in OTHER tabs sharing this cookie jar.
    // We tell the user to use Incognito for true side-by-side.
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Open ${u.email} as a client?\n\n` +
          "A new tab will open signed in as this client. Use the amber " +
          "banner's 'Exit' button to come back to your admin session.\n\n" +
          "Tip: open in a separate Incognito window to keep your admin " +
          "session active in this browser at the same time.",
      )
    ) {
      return;
    }
    setImpersonating(u.user_id);
    try {
      const { data } = await api.post(
        `/admin/users/${u.user_id}/impersonate`,
      );
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't create impersonation link.",
      );
    } finally {
      setImpersonating(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/users", {
          params: {
            q: q || undefined,
            role: role || undefined,
            auth_provider: authProvider || undefined,
            skip,
            limit: LIMIT,
          },
        });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, role, authProvider, skip]);

  return (
    <AdminLayout
      title="Users"
      subtitle="Search, filter and inspect any account on the platform."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-users-filters">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
              <input
                value={q}
                onChange={(e) => {
                  setSkip(0);
                  setQ(e.target.value);
                }}
                placeholder="Search by email, name or user_id..."
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid="admin-users-search-input"
              />
            </div>
            <select
              value={role}
              onChange={(e) => {
                setSkip(0);
                setRole(e.target.value);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid="admin-users-role-select"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r ? `Role: ${r}` : "All roles"}
                </option>
              ))}
            </select>
            <select
              value={authProvider}
              onChange={(e) => {
                setSkip(0);
                setAuthProvider(e.target.value);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid="admin-users-provider-select"
            >
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p ? `Sign-in: ${p}` : "Any sign-in method"}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card className="overflow-hidden" testid="admin-users-table-card">
          {loading ? (
            <div
              className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500"
              data-testid="admin-users-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading users...
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="No users match"
              body="Try a different search or clear the filters."
              testid="admin-users-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">User</th>
                    <th className="text-left px-5 py-2.5 font-medium">Current role</th>
                    <th className="text-left px-5 py-2.5 font-medium">Role</th>
                    <th className="text-left px-5 py-2.5 font-medium">Sign-in</th>
                    <th className="text-left px-5 py-2.5 font-medium">Created</th>
                    <th className="text-left px-5 py-2.5 font-medium">Résumé</th>
                    <th className="px-5 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="admin-users-tbody">
                  {data.items.map((u) => (
                    <tr
                      key={u.user_id}
                      className="hover:bg-slate-50"
                      data-testid={`admin-users-row-${u.user_id}`}
                    >
                      <td className="px-5 py-3">
                        <Link
                          to={`/admin/users/${u.user_id}`}
                          className="flex items-center gap-3 group"
                          data-testid={`admin-users-name-link-${u.user_id}`}
                        >
                          <Avatar
                            user={u}
                            size="sm"
                            testid={`admin-users-avatar-${u.user_id}`}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 group-hover:text-sky-700 truncate">
                              {u.name || u.email.split("@")[0]}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {u.email}
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td className="px-5 py-3">
                        {u.current_role_title ? (
                          <div className="min-w-0">
                            <div className="text-sm text-slate-900 truncate">
                              {u.current_role_title}
                            </div>
                            {u.current_industry && (
                              <div className="text-xs text-slate-500 truncate">
                                {u.current_industry}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">
                            Not yet provided
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill
                          value={u.role || "user"}
                          tone={u.role === "admin" ? "amber" : "slate"}
                        />
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {u.auth_provider || "password"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3">
                        {u.has_resume_pdf ? (
                          <a
                            href={`${BACKEND}/api/archive/resume/${u.user_id}.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                            data-testid={`admin-users-resume-pdf-${u.user_id}`}
                            title="Open the user's résumé PDF"
                          >
                            <FileText className="h-3 w-3" /> PDF
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          {u.role !== "admin" && (
                            <button
                              type="button"
                              onClick={() => openAsClient(u)}
                              disabled={impersonating === u.user_id}
                              title="Open this user's view in a new tab so you can see and interact with their profile as if you were them"
                              className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                              data-testid={`admin-users-impersonate-${u.user_id}`}
                            >
                              {impersonating === u.user_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <UserCheck className="h-3 w-3" />
                              )}
                              Open as client
                            </button>
                          )}
                          <Link
                            to={`/admin/users/${u.user_id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
                            data-testid={`admin-users-view-${u.user_id}`}
                          >
                            View <ChevronRight className="h-3 w-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-users"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
